import {
	PaymentRowTone,
	useGetOrderTransactionsQuery,
	useUpdateOrderDeliveryAddressMutation,
} from '@codezo/graphql';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getMutationError } from '../../lib/mutation-response';
import { EMPTY_ADDRESS, type AddressData } from '../ui/address-form';
import { AddressPicker } from '../ui/address-picker';
import { Button } from '../ui/button';
import { ConfirmDialog } from '../ui/confirm-dialog';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';

interface EditOrderDeliveryAddressModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderId: string;
	/** The client whose saved addresses are offered alongside the custom form. */
	organizationId?: string | null;
	initialValue: {
		addressLine1?: string | null;
		addressLine2?: string | null;
		city?: string | null;
		stateCode?: string | null;
		postalCode?: string | null;
		countryCode?: string | null;
	} | null;
	onSuccess: () => void;
}

/**
 * Corrects where an order ships — the order's own snapshot, not the quote's.
 *
 * The quote's address modal with two differences: it writes the order, and it
 * asks before saving, because this changes where goods go and the client is
 * not told. Completeness and the shipped lock are the server's rules; a refusal
 * comes back with the reason, which is what the toast shows.
 */
export function EditOrderDeliveryAddressModal({
	open,
	onOpenChange,
	orderId,
	organizationId,
	initialValue,
	onSuccess,
}: EditOrderDeliveryAddressModalProps) {
	const [address, setAddress] = useState<AddressData>(EMPTY_ADDRESS);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [updateAddress, { loading }] = useUpdateOrderDeliveryAddressMutation();

	/* Invoices already booked in e-conomic carry the address as their ship-to and
	   are not re-issued, so the confirm says which ones will keep the old one. A
	   booked invoice is a row with a number; history rows are superseded ones. */
	const { data: transactionsData } = useGetOrderTransactionsQuery({
		variables: { orderId },
		skip: !open,
		fetchPolicy: 'no-cache',
	});
	const rows = (transactionsData?.getOrderTransactionsV2 ?? []).filter((row) => !row.isHistory);
	const bookedInvoices = [
		...new Set(
			rows.filter((row) => row.invoiceNumber != null).map((row) => row.invoiceNumber!)
		),
	];
	// Raised but not booked yet — a card checkout open, an invoice being drafted.
	// It captured the address when it was raised, so it will be invoiced with the
	// old one too, and has no number to name yet.
	const paymentInProgress = rows.some(
		(row) => row.invoiceNumber == null && row.tone === PaymentRowTone.Pending
	);
	const mismatches = [
		...(bookedInvoices.length === 1
			? [
					`Invoice #${bookedInvoices[0]} was issued with the current address and won’t be updated.`,
				]
			: bookedInvoices.length > 1
				? [
						`Invoices ${bookedInvoices.map((n) => `#${n}`).join(', ')} were issued with the current address and won’t be updated.`,
					]
				: []),
		...(paymentInProgress
			? ['A payment is in progress and will be invoiced with the current address.']
			: []),
	];

	useEffect(() => {
		if (open) {
			setAddress({
				addressLine1: initialValue?.addressLine1 || '',
				addressLine2: initialValue?.addressLine2 || '',
				city: initialValue?.city || '',
				stateCode: initialValue?.stateCode || '',
				postalCode: initialValue?.postalCode || '',
				countryCode: initialValue?.countryCode || '',
				googlePlaceId: '',
				latitude: null,
				longitude: null,
			});
		}
	}, [open, initialValue]);

	const summary = [
		address.addressLine1,
		address.addressLine2,
		[address.postalCode, address.city].filter(Boolean).join(' '),
		address.countryCode,
	]
		.filter(Boolean)
		.join(', ');

	const handleConfirm = async () => {
		try {
			const { data } = await updateAddress({
				variables: {
					orderId,
					address: {
						addressLine1: address.addressLine1 || undefined,
						addressLine2: address.addressLine2 || undefined,
						city: address.city || undefined,
						stateCode: address.stateCode || undefined,
						postalCode: address.postalCode || undefined,
						countryCode: address.countryCode || undefined,
						googlePlaceId: address.googlePlaceId || undefined,
						latitude: address.latitude,
						longitude: address.longitude,
					},
				},
			});
			const error = getMutationError(data?.updateOrderDeliveryAddress);
			if (error) {
				toast.error(error);
				setConfirmOpen(false);
				return;
			}
			toast.success('Delivery address updated');
			setConfirmOpen(false);
			onOpenChange(false);
			onSuccess();
		} catch {
			toast.error('Failed to update the delivery address');
		}
	};

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Edit delivery address</DialogTitle>
					</DialogHeader>
					<DialogForm onSubmit={() => setConfirmOpen(true)}>
						<DialogBody>
							<AddressPicker
								organizationId={organizationId}
								value={address}
								onChange={setAddress}
								skip={!open}
								autoSelectPrimary={false}
							/>
						</DialogBody>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={loading}>
								Save
							</Button>
						</DialogFooter>
					</DialogForm>
				</DialogContent>
			</Dialog>

			<ConfirmDialog
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				variant="default"
				title="Change delivery address"
				description={
					<>
						Everything still to ship on this order, and invoices raised from now on,
						will use <span className="text-text-1">{summary || 'the new address'}</span>
						. The client isn’t notified, and the quote keeps the address it was accepted
						with.
						{mismatches.length > 0 && (
							// Spans throughout: the dialog description renders as a <p>.
							<span className="mt-3 flex flex-col gap-1.5 rounded-[var(--r-2)] border border-status-warn/30 bg-status-warn/10 p-3 text-text-1">
								<span className="flex items-center gap-2 font-medium text-status-warn">
									<AlertTriangle size={14} className="shrink-0" />
									Won’t match the invoices
								</span>
								{mismatches.map((line) => (
									<span key={line}>{line}</span>
								))}
								<span className="text-text-3">
									The order page will keep showing which invoices have the
									previous address.
								</span>
							</span>
						)}
					</>
				}
				confirmLabel={mismatches.length > 0 ? 'Change anyway' : 'Change address'}
				loading={loading}
				onConfirm={handleConfirm}
			/>
		</>
	);
}
